/* eslint-disable react/require-default-props */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getParentDomain, postMessage } from 'src/utility/Utilities';
import { SOURCE_ACTION, useDialogContext } from 'src/context/DialogContext';
import {
  INBOX_PATH,
  MAIN_PATH,
  MESSAGE_CENTER_CREATE_NEW_MESSAGE,
  MESSAGE_CENTER_INBOX_PATH,
} from 'src/constants/routePaths';
import globalConstants from 'src/constants/globalConstants';
import { Button } from '@vwfs-bronson/bronson-react';
import i18n from '../../../../localization/i18n';
import * as rk from '../../../../localization/resourceKeys';

interface BackLinkProps {
  label: string;
  className?: string;
  mobile?: boolean;
  brand: keyof typeof globalConstants.BRAND_MAPPING;
}

const BackLink: React.FC<BackLinkProps> = ({
  label,
  className = 'c-link__body',
  mobile = false,
  brand,
}: BackLinkProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { state: dialogState, showDialog } = useDialogContext();
  const isNewMessagePath = pathname.endsWith(MESSAGE_CENTER_CREATE_NEW_MESSAGE);
  const isInboxWithMessageSelected = pathname.replace(MESSAGE_CENTER_INBOX_PATH, '').match(/\/\d{1,10}/);

  const [, ENTRY_PATH] = pathname.split('/');

  const onClickHandler = () => {
    // Added for iframes where we need to tell the parent to scrollToTop.
    if (getParentDomain()) {
      postMessage(getParentDomain(), 'scrollToTop', '');
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    if (dialogState.isUpdated) {
      showDialog(SOURCE_ACTION.BACK_TO_HOME);
      return;
    }

    const INBOX_ABSOLUTE_PATH = (() => {
      // if path is the same it means it is running as standalone, required to pass e2e tests
      if (ENTRY_PATH === MAIN_PATH) return `/${MAIN_PATH}/${INBOX_PATH}` as const;

      return `/${ENTRY_PATH}/${MAIN_PATH}/${INBOX_PATH}` as const;
    })();

    if ((isNewMessagePath || isInboxWithMessageSelected) && mobile) {
      navigate(INBOX_ABSOLUTE_PATH);
      return;
    }

    // if rootPath exists that means you came to message center, from another app
    // and when you click go home you need to go there.
    // otherwise just goback to previous page.
    if (window.rootPath) {
      navigate(window.rootPath);
      return;
    }
    navigate(-1);
  };

  if (brand === globalConstants.AUDI_BRAND) {
    return (
      <button type='button' className='c-link' onClick={onClickHandler}>
        <i id='backLinkIcon' className='c-link__icon  c-icon  c-icon--[semantic-back]' aria-hidden='true' role='img' />
        <span className={className}>
          {(isNewMessagePath || isInboxWithMessageSelected) && mobile
            ? i18n.t(rk.BACK_LINK_MESSAGES_TEXT).toString()
            : label}
        </span>
      </button>
    );
  }

  return (
    <Button
      data-tr-component='Back Button Link'
      data-tr-function='Back to messages'
      icon='semantic-back'
      link
      secondary
      small
      truncate
      onClick={onClickHandler}
    >
      {(isNewMessagePath || isInboxWithMessageSelected) && mobile
        ? i18n.t(rk.BACK_LINK_MESSAGES_TEXT).toString()
        : label}
    </Button>
  );
};

export default BackLink;
